import {
  AIActivator,
  EmojiRender,
  EventEmitter,
  getTranslatedText,
  useActivityTracker,
  useUserMSPCheckHook,
  withPlanFeatureGating,
  AI_AGENTS_MAP
} from '@nile/components/src';
import { SVGIcon } from '@superops/super-icons';
import {
  ActionList,
  Button,
  Popover,
  TextStyle
} from '@superops/ui-design-system';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';
import Tippy from '@tippyjs/react';
import 'tippy.js/animations/scale-extreme.css';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';

const ToolTipWrapper = (props) => {
  const { title, children } = props;
  const tippyRef = React.useRef(null);

  if (title) {
    return (
      <div className="position-relative d-in">
        <Tippy
          content={
            <div className="ai-activator-tippy text-white">
              <TextStyle>{title}</TextStyle>
            </div>
          }
          zIndex={3947483000}
          appendTo={document.body}
          animation="scale-extreme"
          allowHTML
          ref={(node) => {
            tippyRef.current = node;
          }}
          className="ai-activator-tippy-wrapper"
        >
          {children}
        </Tippy>
      </div>
    );
  } else {
    return <>{children}</>;
  }
};

const PromptList = (props) => {
  const { prompt, selectedText } = props;
  const intl = useIntl();

  const noSelectedText = isEmpty(selectedText);

  return (
    <ToolTipWrapper
      title={
        noSelectedText &&
        getTranslatedText(intl, 'ai.rephrase.selectedText.empty.message')
      }
    >
      <div
        className={`flex g5 ${
          noSelectedText ? 'opacity5 cursor-not-allowed' : ''
        }`}
      >
        <div className="flex">
          <EmojiRender emoji={prompt?.displayIcon} />
        </div>

        <div>
          <TextStyle>{prompt?.name}</TextStyle>
        </div>
      </div>
    </ToolTipWrapper>
  );
};

const DisabledAction = ({ text }) => {
  return (
    <div className="cursor-not-allowed mw150">
      <TextStyle variation="subdued">{text}</TextStyle>
    </div>
  );
};

const TicketNoteAIActions = (props) => {
  const {
    data,
    onSummaryTypeChange,
    onCopy,
    hideCopyPaste,
    type,
    readOnly,
    hideButtonText,
    prompts,
    editorRef,
    buttonText,
    sendFeedback,
    featureName,
    uiType,
    option,
    workItem,
    summaryType,
    summaryCase,
    variables,
    onOptionClick,
    successText,
    headingText,
    headingIcon,
    copyButtonText = null,
    copySuccessText = null,
    loadingText = null,
    mixpanelEventOnClick = null,
    mixpanelEventOnCopy = null,
    ticket
  } = props;

  const intl = useIntl();

  const aiFeatureHook = AIFeaturesHook();

  const isRephraseEnabled = aiFeatureHook?.getIsAIFeatureEnabled(
    'PROMPT_BUILDER'
  );

  const [selectedText, setSelectedText] = useState(null);
  const [popoverActive, setPopoverActive] = useState(false);

  const updateSelectedText = (updated) => {
    if (selectedText !== updated) {
      setSelectedText(updated);
    }
  };

  const toggleRephrasePopover = () => {
    const updatedState = !popoverActive;
    setPopoverActive(updatedState);
  };

  const rephraseContent = (prompt) => {
    toggleRephrasePopover();
    const variables = {
      featureId: prompt.featureId,
      text: selectedText
    };

    const updatedData = {
      ...data,
      variables: variables
    };

    if (selectedText) {
      if (mixpanelEventOnClick) {
        useActivityTracker(mixpanelEventOnClick);
      }

      EventEmitter.dispatch('openAISummary', {
        data: updatedData,
        type: 'REPHRASE',
        prompts: prompts,
        currentPrompt: prompt,
        onCopy,
        selectedText: selectedText,
        sendFeedback: sendFeedback,
        hideCustomFeedback: true,
        headingText,
        headingIcon,
        copyButtonText,
        copySuccessText,
        loadingText,
        successText: successText || null,
        mixpanelEventOnCopy: {
          event: 'Copy and pasted rephrased content - Ticket note reply ',
          data: {
            ticketId: ticket?.ticketId
          }
        },
        beta: true
      });
    }
  };

  const getSelectedText = () => {
    if (editorRef.current?.FroalaEditorRef) {
      if (editorRef.current?.FroalaEditorRef?.current?.selection) {
        return editorRef.current?.FroalaEditorRef?.current?.selection.text();
      }
      return null;
    } else {
      const range = editorRef.current?.getEditorSelection();
      if (range) {
        const editor = editorRef.current?.getEditor();
        const unprivilegedEditor = editorRef.current?.makeUnprivilegedEditor(
          editor
        );
        // You may now use the unprivilegedEditor proxy methods
        return unprivilegedEditor?.getText(range.index, range.length);
      }
      return null;
    }
  };

  const getSelectedTextOnHover = () => {
    const selected = getSelectedText();
    if (selected) {
      updateSelectedText(selected);
    } else {
      setSelectedText(null);
    }
  };

  return (
    <ToolTipWrapper
      title={getTranslatedText(intl, 'common.rephrase.and.summarize')}
    >
      <div
        id="ai-rephrase-with-note"
        // The getSelectedText is not working in ActionContainer due to some reasons
        // The selection is cleared in editor need to fix it.
        onMouseOver={getSelectedTextOnHover}
      >
        <Popover
          active={popoverActive}
          customClassName="flex"
          activator={
            <Button
              plain
              onClick={toggleRephrasePopover}
              icon={<SVGIcon name={'AIWand'} />}
            />
          }
          onClose={toggleRephrasePopover}
          zIndexOverride={1000}
        >
          <>
            <ActionList
              actionRole="menuitem"
              items={[
                {
                  content: (
                    <AIActivator
                      label={
                        <div className="ml-5">
                          <FormattedMessage id="ai.summary.cta" />
                        </div>
                      }
                      name={AI_AGENTS_MAP.TICKET_CONVERSATION_SUMMARIZE}
                      featureName={AI_AGENTS_MAP.TICKET_CONVERSATION_SUMMARIZE}
                      icon={'AI_TICKET'}
                      disabledTooltip={getTranslatedText(
                        intl,
                        'msp.ai.ticket.summary.disabled.tooltip.message'
                      )}
                      onClick={toggleRephrasePopover}
                      appendTo={document.body}
                      customProps={{
                        summaryCase: 'NOTE',
                        summaryType: 'POINTS',
                        hideCopyPaste: false,
                        onCopy: (content, data) => {
                          EventEmitter.dispatch('insertEditorContent', content);
                        },
                        copyButtonText: getTranslatedText(
                          intl,
                          'common.insert.text'
                        ),
                        copySuccessText: getTranslatedText(
                          intl,
                          'common.inserted'
                        ),
                        variables: {
                          input: {
                            itemId: ticket?.ticketId,
                            itemType: 'TICKET',
                            summaryCase: 'NOTE',
                            summaryType: 'POINTS'
                          }
                        }
                      }}
                    />
                  )
                }
              ]}
            />
            <TextStyle variation="subdued" uppercase>
              <div className="pl10 mbm-5">
                <FormattedMessage id="common.rephrase.text" />
              </div>
            </TextStyle>
            <>
              {!isEmpty(prompts) && isRephraseEnabled ? (
                <ActionList
                  actionRole="menuitem"
                  items={prompts?.map((prompt) => {
                    return {
                      content: (
                        <PromptList
                          prompt={prompt}
                          selectedText={selectedText}
                        />
                      ),
                      onAction: () => {
                        if (!isEmpty(selectedText)) {
                          rephraseContent(prompt);
                        }
                      }
                    };
                  })}
                />
              ) : (
                <div id="empty-propmts-message">
                  <ActionList
                    actionRole="menuitem"
                    items={[
                      {
                        content: (
                          <DisabledAction
                            text={
                              !isRephraseEnabled
                                ? getTranslatedText(
                                    intl,
                                    'ai.rephrase.disabled.tooltip'
                                  )
                                : getTranslatedText(
                                    intl,
                                    'ai.rephrase.prompts.empty.tooltip'
                                  )
                            }
                          />
                        )
                      }
                    ]}
                  />
                </div>
              )}
            </>
          </>
        </Popover>
      </div>
    </ToolTipWrapper>
  );
};

const TicketNoteAIActionsWrapper = (props) => {
  const isMsp = useUserMSPCheckHook();

  if (isMsp) {
    return <TicketNoteAIActions {...props} />;
  }
  return <></>;
};

export default withPlanFeatureGating(
  withErrorBoundary(TicketNoteAIActionsWrapper),
  {
    feature: 'ai_ticket_summary',
    mode: 'hide',
    id: 'TicketNoteAIActionsWrapper'
  }
);
